import { useModalControl } from "@/components/core/modals/use-modal-controller.ts";
import { PdfViewer } from "@/components/pdf-viewer";
import type { Media } from "@/features/dashboard/types/media.ts";
import { formatDate } from "@/utils/format-date.ts";
import { formatNumber } from "@/utils/format-number";
import { DocumentEditor } from "@onlyoffice/document-editor-react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { Button } from "../core/buttons/button";
import { ScrollArea } from "../core/scroller";

export function FileViewer({
	attachment,
	token,
	options,
}: {
	attachment: Media;
	token?: string;
	options?: Record<string, string | number>;
}) {
	const { closeModal } = useModalControl();
	const config = {
		...options,
		token,
	};

	return (
		<div className="flex flex-col w-full z-[99]">
			<div className="flex flex-row items-center justify-between w-full h-[40px] px-4 bg-slate-50 border-b border-slate-200">
				<span className="text-sm font-bold text-foreground-dark">{attachment.name}</span>
				<div className="inline-flex flex-row w-auto gap-3" />
			</div>
			<div className="flex grow shrink flex-row w-full h-full">
				<PanelGroup className="flex flex-row grow w-full" direction="horizontal">
					<Panel defaultSize={20} minSize={20} maxSize={20} className="flex flex-col shrink-0 grow p-4 max-w-[420px]">
						{/* <div className="flex shrink-0 flex-row items-center justify-between w-full h-[50px] px-4 border-b border-slate-200">
              <span className="text-base font-bold text-foreground-dark">{attachment.name}</span>
              <button
                onClick={() => {
                  closeModal();
                }}
                className="flex w-[20px] h-[20px] ring-offset-background transition-opacity outline-none border-none ring-0 text-foreground opacity-40 hover:opacity-100 disabled:pointer-events-none"
                type="button"
              >
                <IconX size={20} stroke={1.5} />
              </button>
            </div>
*/}
						{/* <ScrollArea className="h-[calc(100svh - 120px)]"> */}
						<ScrollArea>
							<div className="grid grid-cols-12 w-full gap-3 overflow-y-auto">
								<div className="col-span-12 w-full">
									<div className="flex flex-col w-full shrink-0 border-b border-border/50 last:border-b-0">
										<span className="text-base font-semibold text-foreground-dark leading-tight">Name</span>
										<span className="text-base text-foreground/70 leading-tight">{attachment.name || attachment.file_name}</span>
									</div>
								</div>
								<div className="col-span-12 w-full">
									<div className="flex flex-col w-full shrink-0 border-b border-border/50 last:border-b-0">
										<span className="text-base font-semibold text-foreground-dark leading-tight">File Size</span>
										<span className="text-base text-foreground/70 leading-tight">{formatNumber(attachment.size, "0,0b")}</span>
									</div>
								</div>
								<div className="col-span-12 w-full">
									<div className="flex flex-col w-full shrink-0 border-b border-border/50 last:border-b-0">
										<span className="text-base font-semibold text-foreground-dark leading-tight">Date Uploaded</span>
										<span className="text-base text-foreground/70 leading-tight">{formatDate(attachment.updated_at, "PPpp")}</span>
									</div>
								</div>
							</div>
						</ScrollArea>

						<div className="flex flex-row w-full gap-2 mt-auto">
							<Button onClick={closeModal} className="w-full" variant="ghost">
								Close
							</Button>
						</div>
					</Panel>

					<PanelResizeHandle className="group flex justify-center items-center w-[12px] border-r border-l border-border/50 bg-white">
						<div className="inline-flex w-[4px] h-[40px] rounded-sm bg-foreground/50 group-hover:bg-foreground/90" />
					</PanelResizeHandle>

					<Panel defaultSize={80} minSize={80} maxSize={80} className="flex flex-col shrink grow w-full h-full bg-slate-100 !pt-0">
						<div style={{ height: "calc(100vh - 40px)" }}>
							{["application/zip", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(
								attachment.mime_type,
							) && (
								<DocumentEditor
									id="onlyoffice-editor"
									documentServerUrl={import.meta.env.VITE_DOC_SERVER_URL} // Update with your document server URL
									config={config}
									width="100%"
									height="100%"
								/>
							)}
							{["application/pdf", "application/octet-stream"].includes(attachment.mime_type) && (
								<PdfViewer attachment={attachment} file={attachment.original_url ?? attachment} documents={attachment} />
							)}
							{["image/jpeg", "image/png", "png", "jpg", "jpeg"].includes(attachment.mime_type) && (
								<img src={attachment.original_url} alt={attachment.name} className="max-w-full max-h-full object-contain" />
							)}

							{["video/mp4"].includes(attachment.mime_type) && (
								// biome-ignore lint/a11y/useMediaCaption: <no>
								<video controls width="100%">
									<source src={attachment.original_url} type="video/mp4" />
									Your browser does not support the video tag.
								</video>
							)}
							{!attachment.original_url && <PdfViewer attachment={attachment} file={attachment.original_url ?? attachment} />}
						</div>
					</Panel>
				</PanelGroup>
			</div>
		</div>
	);
}
